/**
 * ProviderPerformancePanel — Compact lead analytics widget
 *
 * Merged: Performance bar + SLA/Escalation/Guard inline + Funnel (single card)
 * Target: ~35% height reduction vs original two-card layout
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  TrendingUp, Users, Trophy, XCircle, Clock, AlertTriangle,
  Shield, Activity, ChevronRight, RefreshCw, Loader2
} from 'lucide-react';
import api from '../utils/api';


const SLA_GOOD = 30;
const SLA_WARN = 120;

const FUNNEL_STAGES = [
  { key: 'NEW', label: 'New', color: 'bg-slate-500' },
  { key: 'CONTACTED', label: 'Contacted', color: 'bg-sky-500' },
  { key: 'QUALIFIED', label: 'Qualified', color: 'bg-indigo-500' },
  { key: 'OFFER_SENT', label: 'Offer Sent', color: 'bg-violet-500' },
  { key: 'NEGOTIATION', label: 'Negotiation', color: 'bg-amber-500' },
  { key: 'WON', label: 'Won', color: 'bg-emerald-500' },
  { key: 'LOST', label: 'Lost', color: 'bg-red-500' },
];

const SLABadge = ({ minutes }) => {
  if (minutes === 0) return <span className="text-[10px] text-slate-400">--</span>;
  const cls = minutes <= SLA_GOOD ? 'text-emerald-600' : minutes <= SLA_WARN ? 'text-amber-600' : 'text-red-500';
  return <span className={`text-[10px] font-semibold ${cls}`} data-testid="sla-badge-good">{minutes}m</span>;
};

const EscalationIndicator = ({ rate }) => {
  const pct = (rate * 100).toFixed(1);
  if (rate === 0) return <span className="text-[10px] text-slate-400">0%</span>;
  const cls = rate <= 0.05 ? 'text-emerald-600' : rate <= 0.15 ? 'text-amber-600' : 'text-red-600';
  return <span className={`text-[10px] font-semibold ${cls}`} data-testid="escalation-low">{pct}%</span>;
};

export const ProviderPerformancePanel = ({ clinicId, filters = {}, onSummaryUpdate }) => {
  const [perf, setPerf] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ clinic_id: clinicId });
      if (filters.source) params.append('source', filters.source);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
      const qs = params.toString();
      const [perfRes, funnelRes] = await Promise.all([
        api.get(`/v2/analytics/provider-performance?${qs}`),
        api.get(`/v2/analytics/lead-funnel?${qs}`),
      ]);
      if (perfRes.ok) setPerf(perfRes.data);
      if (funnelRes.ok) setFunnel(funnelRes.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [clinicId, filters.source, filters.assigned_to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Emit summary for collapsed strip (must be before conditional returns)
  useEffect(() => {
    if (perf && onSummaryUpdate) {
      const rate = perf.total_leads > 0 ? ((perf.won_leads / perf.total_leads) * 100).toFixed(1) : '0.0';
      onSummaryUpdate({
        totalLeads: perf.total_leads,
        wonLeads: perf.won_leads,
        lostLeads: perf.lost_leads,
        conversionRate: rate,
      });
    }
  }, [perf, onSummaryUpdate]);

  if (loading) {
    return (
      <Card data-testid="performance-panel-loading">
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-sky-500" />
        </CardContent>
      </Card>
    );
  }

  if (!perf) return null;

  const funnelData = funnel?.funnel || {};
  const maxCount = Math.max(...Object.values(funnelData), 1);
  const conversionRate = perf.total_leads > 0 ? ((perf.won_leads / perf.total_leads) * 100).toFixed(1) : '0.0';

  return (
    <Card data-testid="performance-panel">
      <CardContent className="px-4 py-3">
        {/* Header row: title + inline KPIs + refresh */}
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5 text-sky-600" />
            <span className="text-sm font-semibold text-slate-800">Lead Performance</span>
          </div>

          {/* Inline KPI chips */}
          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-sky-50" data-testid="metric-total-leads">
              <Users className="h-3 w-3 text-sky-600" />
              <span className="text-xs font-bold text-sky-700">{perf.total_leads}</span>
              <span className="text-[9px] text-sky-500">total</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50" data-testid="metric-active-leads">
              <TrendingUp className="h-3 w-3 text-indigo-600" />
              <span className="text-xs font-bold text-indigo-700">{perf.active_leads}</span>
              <span className="text-[9px] text-indigo-500">active</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50" data-testid="metric-won-leads">
              <Trophy className="h-3 w-3 text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700">{perf.won_leads}</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-50" data-testid="metric-lost-leads">
              <XCircle className="h-3 w-3 text-red-500" />
              <span className="text-xs font-bold text-red-600">{perf.lost_leads}</span>
            </div>
            <Badge variant="outline" className="text-[10px] font-bold ml-1" data-testid="conversion-rate">{conversionRate}%</Badge>
            <button onClick={fetchData} className="p-1 hover:bg-slate-100 rounded-full" data-testid="refresh-analytics">
              <RefreshCw className="h-3 w-3 text-slate-400" />
            </button>
          </div>
        </div>

        {/* SLA + Escalation + Guard — inline micro stat row */}
        <div className="flex items-center gap-4 mb-2.5 text-[10px] text-slate-500">
          <span className="flex items-center gap-1">
            <Clock className="h-2.5 w-2.5" /> SLA <SLABadge minutes={perf.avg_first_response_minutes} />
          </span>
          <span className="flex items-center gap-1" data-testid="escalation-indicator">
            <AlertTriangle className="h-2.5 w-2.5" /> Esc. <EscalationIndicator rate={perf.escalation_rate} />
          </span>
          <span className="flex items-center gap-1" data-testid="guard-flag-indicator">
            <Shield className="h-2.5 w-2.5" /> Guard {(perf.guard_flag_rate * 100).toFixed(1)}%
          </span>
          {perf.avg_thread_duration_hours > 0 && (
            <span className="flex items-center gap-1">
              <Clock className="h-2.5 w-2.5" /> Avg. {perf.avg_thread_duration_hours}h
            </span>
          )}
        </div>

        {/* Funnel — compressed horizontal bars */}
        <div className="space-y-1" data-testid="lead-funnel-viz">
          <div className="flex items-center gap-1 mb-1">
            <ChevronRight className="h-3 w-3 text-violet-500" />
            <span className="text-[11px] font-semibold text-slate-700">Funnel</span>
          </div>
          {FUNNEL_STAGES.map((stage) => {
            const count = funnelData[stage.key] || 0;
            const width = maxCount > 0 ? Math.max((count / maxCount) * 100, 3) : 3;
            return (
              <div key={stage.key} className="flex items-center gap-2" data-testid={`funnel-stage-${stage.key}`}>
                <span className="text-[10px] text-slate-500 w-[64px] text-right shrink-0 leading-none">{stage.label}</span>
                <div className="flex-1 h-4 bg-slate-50 rounded overflow-hidden relative">
                  <div
                    className={`h-full ${stage.color} rounded transition-all duration-500 ease-out flex items-center`}
                    style={{ width: `${width}%` }}
                  >
                    {count > 0 && (
                      <span className="text-[9px] font-bold text-white pl-1.5 leading-none">{count}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProviderPerformancePanel;
